import {
  NonEmptyFeature,
  ProgramDecision,
} from "../../../../../common/api/decimal/methods/course.ts";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useRef } from "react";
import Accordion from "../../../../../common/core-ui/Accordion.tsx";
import { Dialog } from "../../../../../common/core-ui/Dialog.tsx";
import styles from "./ProgramSwitcherDialog.module.scss";
import { clsx } from "clsx";
import MiscellaneousCheckmarkSm from "../../../../../common/icons/MiscellaneousCheckmarkSm.tsx";
import MiscellaneousXSmall from "../../../../../common/icons/MiscellaneousXSmall.tsx";
import SocialStarFill from "../../../../../common/icons/SocialStarFill.tsx";
import Markdown from "react-markdown";
import useTermOptDeadline from "../useTermOptDeadline.tsx";
import useSwitchProgram from "../useSwitchProgram.tsx";
import DaysRemainingMessage from "./DaysRemainingMessage.tsx";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton.tsx";
import { SingleSpaceLiteral } from "common/core-ui/SingleSpaceLiteral.tsx";
import { useCoursesLoaderData, useProgramDetails } from "../coursesLoader.ts";
import useProgramPricing from "../useProgramPricing.tsx";
import DotLoader from "../../../../../common/core-ui/DotLoader.tsx";

const buildCheckboxId = (plan: ProgramDecision) => `${plan}-checkbox`;
const buildDetailsId = (plan: ProgramDecision) => `${plan}-details`;
const buildPriceId = (plan: ProgramDecision) => `${plan}-price`;

interface Link {
  label: string;
  href: string;
}

interface Plan {
  type: ProgramDecision;
  links: Link[];
  features: NonEmptyFeature[];
  name: string;
  price: string;
  priceDrawerDescription: string;
}

function PlanLink({ label, href }: Link) {
  return (
    <BaseLinkOrButton className="vst-link" href={href}>
      {label}
    </BaseLinkOrButton>
  );
}

export default function ProgramSwitcherDialog({
  onRequestClose,
  onShowConfirm,
}: {
  onRequestClose: () => void;
  onShowConfirm: () => void;
}) {
  const {
    scope,
    userEffectiveProgram,
    ea: {
      updateShippingLink,
      personalizedValueSheet,
      features: eaFeatures,
      name: eaProgramName,
    },
    ia: { features: iaFeatures, name: iaProgramName },
    np: {
      features: npFeatures,
      showPVSActualMarketPrice: npShowPVSActualMarketPrice,
    },
    personalizedValueSheet: {
      showPriceDrawer,
      showPricing,
      showCreditHourBreakdownInPvsDrawer,
    },
  } = useProgramDetails();
  const { learnerRequiredMaterials } = useCoursesLoaderData();
  const intl = useIntl();
  const choicesRef = useRef<HTMLDivElement>(null);
  const { optDeadline } = useTermOptDeadline();
  // This does not use a Form because it leads to divergent handling between
  // submittable form radio choices and the separate opt-out button (which
  // is inside the form but not submitted in the same way)
  const { changeProgram, isProgramChangeProcessing } =
    useSwitchProgram(onRequestClose);

  const { ea, ia, np } = useProgramPricing();

  const getEquitableAccessOnlyLinks = (): Link[] => {
    const links = [];
    if (updateShippingLink) {
      links.push({
        label: intl.formatMessage({
          defaultMessage: "Update Delivery Preferences",
        }),
        href: updateShippingLink,
      });
    }
    return links;
  };

  const plans: Plan[] = [];
  const noPlan: Plan = {
    type: "no_program",
    links: [],
    features: npFeatures,
    name: intl.formatMessage({ defaultMessage: "Opt Out of All Materials" }),
    price:
      npShowPVSActualMarketPrice && np.price
        ? intl.formatNumber(np.price, {
            style: "currency",
            currency: "USD",
          })
        : "",
    priceDrawerDescription: npShowPVSActualMarketPrice
      ? intl.formatMessage({
          defaultMessage:
            "Total reflects the sum of all materials if purchased individually at retail cost. Pricing may vary by format and seller.",
        })
      : intl.formatMessage({
          defaultMessage:
            "Actual costs will vary depending on format, seller, and availability.",
        }),
  };
  const iaPlan: Plan = {
    type: "inclusive_access",
    links: [],
    features: iaFeatures,
    name: iaProgramName,
    price: ia.price
      ? intl.formatNumber(ia.price, {
          style: "currency",
          currency: "USD",
        })
      : "",
    priceDrawerDescription: intl.formatMessage(
      {
        defaultMessage:
          "Program total reflects the sum of all materials that are part of {name} for the term.",
      },
      { name: iaProgramName }
    ),
  };
  const eaPlan: Plan = {
    type: "equitable_access",
    links: [],
    features: eaFeatures,
    name: eaProgramName,
    price: ea.price
      ? intl.formatNumber(ea.price, {
          style: "currency",
          currency: "USD",
        })
      : "",
    priceDrawerDescription: intl.formatMessage(
      {
        defaultMessage:
          "Program total reflects the total program cost of {name} for the term.",
      },
      { name: eaProgramName }
    ),
  };
  if (scope === "ea_only") {
    plans.push({ ...eaPlan, links: getEquitableAccessOnlyLinks() }, noPlan);
  } else if (scope === "flexible") {
    plans.push(eaPlan, iaPlan, noPlan);
  }

  // If we cannot get calculated pricing, do not show anything in the pricing
  // section. If we are showing actual market price for the no program option,
  // all program prices are required. Otherwise, only the prices for the other
  // programs are necessary.
  // NP market price loads asynchronously and shows a loader while waiting,
  // so it is never required to unblock display of the other plan prices.
  const areRequiredPricesAvailable = plans
    .filter((plan) => plan.type !== "no_program")
    .every((plan) => plan.price);

  const renderPlanPrice = (price: string, type: ProgramDecision) => (
    <FormattedMessage
      defaultMessage="{price}{suffix}"
      values={{ price, suffix: type === "equitable_access" ? "" : "*" }}
    />
  );

  const clickHandler = (plan: ProgramDecision) => {
    const checkbox = document.getElementById(
      buildCheckboxId(plan)
    ) as HTMLInputElement;
    if (checkbox) {
      checkbox.checked = true;
      // Keep focus on a visible element. The "Select This Program" button is
      // display:none on the selected card, so without this focus would drop
      // to <body> after the CSS swap.
      checkbox.focus();
    }
  };

  return (
    <Dialog
      canClose={!isProgramChangeProcessing}
      darkHeader
      onRequestClose={onRequestClose}
      title={intl.formatMessage({
        defaultMessage: "Select Material Access Program",
      })}
      customWidth={plans.length > 2 ? "1022px" : "700px"}
      primaryButton={{
        loading: isProgramChangeProcessing,
        loadingContext: intl.formatMessage({
          defaultMessage: "Course Materials Program",
        }),
        label: intl.formatMessage({ defaultMessage: "Confirm Selection" }),
        onClick: () => {
          // I have seen a11y concerns with having the submit button go disabled.
          // Let the loader button do its thing, but prevent duplicate submissions
          if (isProgramChangeProcessing) {
            return;
          }

          const selectedProgram: ProgramDecision =
            choicesRef.current!.querySelector<HTMLInputElement>(
              "input:checked"
            )!.value as ProgramDecision;
          if (userEffectiveProgram !== selectedProgram) {
            if (selectedProgram === "no_program") {
              onShowConfirm();
            } else {
              changeProgram(selectedProgram);
            }
          } else {
            onRequestClose();
          }
        },
      }}
    >
      {optDeadline && (
        <div className={styles.header}>
          <DaysRemainingMessage />
          {scope === "ea_only" && personalizedValueSheet && (
            <>
              <SingleSpaceLiteral />
              <BaseLinkOrButton
                className="vst-link"
                href={personalizedValueSheet}
                target="_blank"
              >
                <FormattedMessage defaultMessage="View More Details" />
              </BaseLinkOrButton>
            </>
          )}
        </div>
      )}
      <fieldset className="vst-fieldset">
        <legend className={styles.choicesLegend}>
          <FormattedMessage defaultMessage="Select Material Access Program" />
        </legend>
        <div
          className={clsx(
            styles.choices,
            showPriceDrawer && styles.withPriceDrawer,
            showPricing && styles.withPricing
          )}
          ref={choicesRef}
        >
          {plans.map(
            ({
              type,
              links,
              features,
              name,
              price,
              priceDrawerDescription,
            }) => (
              // This clickable div is intentional. We should allow this div to be clicked and
              // remove tab focus so that focus will move to the radio button instead
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                className={styles.radioBoxOuter}
                key={type}
                onClick={() => clickHandler(type)}
              >
                {type === "equitable_access" && (
                  <div className={styles.mostPopularTag}>
                    <SocialStarFill />
                    <FormattedMessage defaultMessage="Most Popular" />
                  </div>
                )}
                <span className={styles.radioInput}>
                  <input
                    id={buildCheckboxId(type)}
                    name="program"
                    type="radio"
                    value={type}
                    defaultChecked={userEffectiveProgram === type}
                    disabled={isProgramChangeProcessing}
                    aria-describedby={clsx(
                      showPricing &&
                        areRequiredPricesAvailable &&
                        buildPriceId(type),
                      buildDetailsId(type)
                    )}
                  />
                  <label
                    htmlFor={buildCheckboxId(type)}
                    className={styles.planHeader}
                  >
                    {name}
                  </label>
                </span>
                {showPricing && areRequiredPricesAvailable && (
                  <div id={buildPriceId(type)} className={styles.planPrice}>
                    <div className={styles.planPriceText}>
                      {price ? (
                        renderPlanPrice(price, type)
                      ) : type === "no_program" ? (
                        np.waiting ? (
                          <div className={styles.marketPriceLoaderContainer}>
                            <DotLoader
                              dotCount={3}
                              color="inherit"
                              className={styles.marketPriceLoader}
                            />
                          </div>
                        ) : npShowPVSActualMarketPrice ? (
                          <FormattedMessage defaultMessage="Price Unavailable*" />
                        ) : (
                          <FormattedMessage defaultMessage="Market Price*" />
                        )
                      ) : null}
                    </div>
                  </div>
                )}
                <div className={styles.planBody}>
                  <ul id={buildDetailsId(type)} className={styles.planDetails}>
                    {features.map((feature) => (
                      <li key={feature.text} className="vst-icon-with-text">
                        {/* Custom bullet available in feature data, but not supported yet */}
                        {type === "no_program" ? (
                          <MiscellaneousXSmall className={styles.xIcon} />
                        ) : (
                          <MiscellaneousCheckmarkSm />
                        )}
                        <Markdown
                          allowedElements={["strong", "em", "a", "span", "p"]}
                          components={{
                            // Need outer wrapper element, but you can't put a p in a label,
                            // nor do we want the default p margins
                            p: "span",
                            a: (props) => (
                              // External content and provided by spread
                              // eslint-disable-next-line jsx-a11y/anchor-has-content
                              <a
                                className="vst-link"
                                target="_blank"
                                rel="noopener noreferrer"
                                {...props}
                              />
                            ),
                          }}
                        >
                          {/* remark-variables looks like it would do this, but seems unsupported/unmaintained */}
                          {/* Dynamic price source TBD */}
                          {feature.text.replace("{{price}}", "UNKNOWN PRICE")}
                        </Markdown>
                      </li>
                    ))}
                    {links.map((link) => (
                      <li key={link.label} className="vst-icon-with-text">
                        <MiscellaneousCheckmarkSm />
                        <PlanLink {...link} />
                      </li>
                    ))}
                  </ul>
                  {/*
                  Selected vs unselected state is swapped via CSS based on the
                  radio's :checked state (display:none on the inactive element
                  removes it from the a11y tree). The button activates via the
                  parent .radioBoxOuter onClick by bubble, and clickHandler
                  moves focus to the radio to avoid focus loss when the button
                  hides itself.
                */}
                  <div className={styles.selectionState}>
                    <div className={styles.selectedProgramTag}>
                      <MiscellaneousCheckmarkSm />
                      <FormattedMessage defaultMessage="Current Selection" />
                    </div>

                    <BaseLinkOrButton
                      className={clsx(
                        "vst-button-pill",
                        "vst-button-variant-outline",
                        styles.selectProgramButton
                      )}
                    >
                      {type === "no_program" ? (
                        <FormattedMessage defaultMessage="Opt Out of All Materials" />
                      ) : (
                        <FormattedMessage defaultMessage="Select This Program" />
                      )}
                    </BaseLinkOrButton>
                  </div>
                  {showPriceDrawer && areRequiredPricesAvailable && (
                    // Prevent the accordion toggle (and anything else inside the
                    // price drawer) from bubbling up and selecting this program.
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div
                      className={styles.priceDrawer}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Accordion
                        iconSide="right"
                        initiallyExpanded={false}
                        tagName="div"
                        title={
                          type === "no_program"
                            ? intl.formatMessage({
                                defaultMessage: "View Details",
                              })
                            : intl.formatMessage({
                                defaultMessage: "My Program Value",
                              })
                        }
                      >
                        {price && (
                          <p className={styles.programTotal}>
                            {type === "no_program" ? (
                              <FormattedMessage defaultMessage="Individual Purchase Total" />
                            ) : (
                              <FormattedMessage defaultMessage="Program Total" />
                            )}
                            <span>{renderPlanPrice(price, type)}</span>
                          </p>
                        )}
                        {type === "equitable_access" &&
                          showCreditHourBreakdownInPvsDrawer &&
                          ea.totalCreditHours !== null && (
                            <>
                              <p className={styles.creditHourBreakdown}>
                                <FormattedMessage defaultMessage="Credit hours this term" />
                                <span>
                                  <FormattedNumber
                                    value={ea.totalCreditHours}
                                  />
                                </span>
                              </p>
                              <p className={styles.creditHourBreakdown}>
                                <FormattedMessage defaultMessage="Number of required materials" />
                                <span>
                                  <FormattedNumber
                                    value={learnerRequiredMaterials.length}
                                  />
                                </span>
                              </p>
                            </>
                          )}
                        <p className={styles.programDescription}>
                          {priceDrawerDescription}
                        </p>
                      </Accordion>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </fieldset>
      {(showPricing || showPriceDrawer) && areRequiredPricesAvailable && (
        <div className={styles.footer}>
          <FormattedMessage defaultMessage="* Prices may vary depending on your course schedule, instructor adoptions, format selection, and opting preferences." />
        </div>
      )}
    </Dialog>
  );
}
